/**
 * Contraste WCAG entre cores em hex (`#rgb` ou `#rrggbb`).
 *
 * Existe para o tema por marca: a cor vem da configuração do white label e
 * pode ser qualquer uma — inclusive branca, o que deixava o item ativo do
 * menu (fundo da marca + texto branco) ilegível.
 */

/** Texto sobre fundo escuro. */
export const LIGHT_TEXT = "#ffffff"
/**
 * Texto sobre fundo claro. Preto puro: entre branco e preto, um dos dois
 * sempre passa 4.5:1 com qualquer fundo; um quase-preto falharia nos cinzas
 * médios.
 */
export const DARK_TEXT = "#000000"

const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i

const parseHex = (
  color: string
): [number, number, number] | null => {
  const match = HEX_COLOR.exec(color.trim())
  if (!match) return null
  const digits =
    match[1].length === 3
      ? match[1]
          .split("")
          .map((digit) => digit + digit)
          .join("")
      : match[1]
  return [0, 2, 4].map((start) =>
    parseInt(digits.slice(start, start + 2), 16)
  ) as [number, number, number]
}

const channelLuminance = (channel: number) => {
  const value = channel / 255
  return value <= 0.03928
    ? value / 12.92
    : ((value + 0.055) / 1.055) ** 2.4
}

const relativeLuminance = ([red, green, blue]: [
  number,
  number,
  number,
]) =>
  0.2126 * channelLuminance(red) +
  0.7152 * channelLuminance(green) +
  0.0722 * channelLuminance(blue)

/** Razão de contraste WCAG (1 a 21). `null` se alguma cor não for hex. */
export const contrastRatio = (
  first: string,
  second: string
): number | null => {
  const firstRgb = parseHex(first)
  const secondRgb = parseHex(second)
  if (!firstRgb || !secondRgb) return null
  const [lighter, darker] = [
    relativeLuminance(firstRgb),
    relativeLuminance(secondRgb),
  ].sort((a, b) => b - a)
  return (lighter + 0.05) / (darker + 0.05)
}

/**
 * Cor de texto legível sobre `background`: a de maior contraste entre
 * {@link LIGHT_TEXT} e {@link DARK_TEXT}. Sem cor em hex, devolve o branco
 * (comportamento anterior, pensado para o verde padrão do tema).
 */
export const readableTextColor = (
  background?: string | null
): string => {
  if (!background) return LIGHT_TEXT
  const withLight = contrastRatio(background, LIGHT_TEXT)
  const withDark = contrastRatio(background, DARK_TEXT)
  if (withLight === null || withDark === null)
    return LIGHT_TEXT
  return withDark > withLight ? DARK_TEXT : LIGHT_TEXT
}
